/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { EPR_TYPE, ISiteMngData } from '../types';
import { Roles, formatSitesForTranslation, saveSites, translateSites, getSites } from '../helpers';
import TranslateModal from '../TranslateModal';
import AlertModal from '../Modals/AlertModal';
import { useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '../../../logger';

interface IProps {
  data: ISiteMngData[];
  rowSelection: any;
  setRowSelection: any;
  editedUids: string[];
  setEditedUids: React.Dispatch<React.SetStateAction<string[]>>;
  selectedEprName: string;
  setData: (newData: ISiteMngData[]) => void;
  setCloneData: (newData: ISiteMngData[]) => void;
  role: string;
  setIsLoading: any;
  onDataRefetched?: (savedUids: string[]) => void;
}

const SOURCE_SMA = 'SMA';

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const FooterActions: React.FC = (props: IProps) => {
  const rowSelection = props.rowSelection;
  const editedUids = props.editedUids;
  const [isTranslateOpen, setIsTranslateOpen] = useState<boolean>(false);
  const selectedEpr = props.selectedEprName || '';
  const selectedFilters = useSelector((state: any) => state?.sma?.selectedFilters);
  const selectedProtocol = useSelector((state: any) => state?.sma?.selectedProtocol);
  const selectedProtocolItemId = useSelector((state: any) => state?.sma?.selectedProtocolItemId);
  const fieldOptions = useSelector((state: any) => state?.sma?.fieldOptions);
  const eprFieldOptions = useSelector((state: any) => state?.sma?.eprFieldOptions);
  const userName = useSelector((state: any) => state?.sma?.userName);
  const SmaBatchSize = parseInt(process.env.SMA_BATCH_SIZE || '5', 10);
  const [alertModal, setAlertModal] = useState<{ open: boolean; message: string; title?: string }>({
    open: false,
    message: '',
  });

  /** Merge refetched data with unsaved local edits (O(n) via Map lookup). */
  const mergeRefetchedWithUnsaved = (
    refetchedData: ISiteMngData[],
    unsavedRows: ISiteMngData[]
  ): ISiteMngData[] => {
    const refetchedSmaRowIds = new Set(refetchedData.map((r) => r.smaRowId));
    const unsavedBySmaRowId = new Map(unsavedRows.map((item) => [item.smaRowId, item]));
    const merged = refetchedData.map((refetchedItem) => {
      const unsaved = unsavedBySmaRowId.get(refetchedItem.smaRowId);
      return unsaved ? { ...unsaved, uid: refetchedItem.uid } : refetchedItem;
    });
    const newLocal = unsavedRows.filter((item) => !refetchedSmaRowIds.has(item.smaRowId));
    return [...merged, ...newLocal];
  };

  /**
   * Saves selected rows. If no Rows are selected all edited rows are saved
   */
  const onSaveClick = async (isPublish: boolean) => {
    if (editedUids.length > 0) {
      let uidsToSave: string[] = [];
      const isRowsSelected = Object.keys(rowSelection).length > 0;
      if (isRowsSelected) {
        // Filter only edited ids
        const selectedRowIds = Object.keys(rowSelection);
        selectedRowIds.forEach((selectedId) => {
          if (editedUids.includes(selectedId)) {
            uidsToSave.push(selectedId);
          }
        });
      } else {
        uidsToSave = editedUids;
      }
      if (uidsToSave.length > 0) {
        try {
          props.setIsLoading(true);
          const sitesToSave = cloneDeep(props.data.filter((item) => uidsToSave.includes(item.uid)));
          const EprId = selectedFilters.EPR === 'global' ? null : selectedFilters.EPR;
          let protocolItemId: any = null;
          if (selectedFilters.EPR === 'global') {
            protocolItemId = selectedProtocolItemId;
          }
          sitesToSave.forEach((item: any) => {
            item.isPublished = isPublish;
            item.siteDetailTarget = selectedEpr;
            item.EprId = EprId;
            item.ProtocolItemId = protocolItemId;
            item.radiusInMiles = item?.radiusInMiles || '100';
            item.threshold = item?.threshold || '100';
            item.priority = `${item?.priority || ''}`;
            item.contactEmail = `${item?.contactEmail || ''}`;
            item.studyStaffEmails = `${item?.studyStaffEmails || ''}`;
            item.UpdatedBy = userName;
          });
          // Save sites in batches of 5
          let saveSitesOperation = true;
          let count = 0;
          const batchSize = SmaBatchSize;
          for (let i = 0; i < sitesToSave.length; i += batchSize) {
            const batch = sitesToSave.slice(i, i + batchSize);
            const result = await saveSites(batch);
            if (!result) {
              saveSitesOperation = false;
              break;
            } else {
              count += batch.length;
            }
          }
          if (saveSitesOperation) {
            props.setEditedUids([]);
            // Refetch Data
            if (selectedProtocol && selectedFilters.language) {
              const refetchedData = await getSites(
                selectedProtocol,
                selectedFilters.language,
                selectedFilters.EPR
              );
              props.setData(refetchedData);
              props.setCloneData(refetchedData);
              props.setRowSelection({});
            }
            alert(count + ' Sites Saved');
          } else {
            logger.error('Unable to process all sites to staging', {
              respose: `Only ${sitesToSave.length - count} sites has been saved`,
              payload: {},
            });
          }
          props.setIsLoading(false);
        } catch (_error) {
          props.setIsLoading(false);
        }
      }
    } else {
      alert('No Sites selected or edited to Save');
    }
  };

  const onSaveAndPublishClick = async () => {
    if (editedUids.length === 0) {
      setAlertModal({
        open: true,
        message:
          'Unable to save and publish: No sites have been selected or edited. Please select at least one site or make changes to existing sites before attempting to save and publish.',
        title: 'No Sites to Save and Publish',
      });
      return;
    }

    const editedSet = new Set(editedUids);
    const selectedRowIds = Object.keys(rowSelection);
    const isRowsSelected = selectedRowIds.length > 0;

    let uidsToSave: string[];
    let nonEditedSelectedRows: string[] = [];

    if (isRowsSelected) {
      nonEditedSelectedRows = selectedRowIds.filter((id) => !editedSet.has(id));
      uidsToSave = selectedRowIds.filter((id) => editedSet.has(id));
      if (uidsToSave.length === 0) {
        setAlertModal({
          open: true,
          message:
            'Validation error: You are trying to Save and Publish a row which is not edited. Please ensure that all selected rows have been edited before saving and publishing, or deselect the rows that have not been modified.',
          title: 'Cannot Save and Publish Unedited Rows',
        });
        return;
      }
    } else {
      uidsToSave = editedUids;
    }

    const uidsToSaveSet = new Set(uidsToSave);
    const sitesToSaveAndPublish = props.data.filter((item) => uidsToSaveSet.has(item.uid));

    try {
      // Step 1: Save the sites
      props.setIsLoading(true);
      const sitesToSave = cloneDeep(sitesToSaveAndPublish);
      const EprId = selectedFilters.EPR === 'global' ? null : selectedFilters.EPR;
      let protocolItemId: any = null;
      if (selectedFilters.EPR === 'global') {
        protocolItemId = selectedProtocolItemId;
      }
      sitesToSave.forEach((item: any) => {
        item.isPublished = false; // Save first, don't publish yet
        item.siteDetailTarget = selectedEpr;
        item.EprId = EprId;
        item.ProtocolItemId = protocolItemId;
        item.radiusInMiles = item?.radiusInMiles || '100';
        item.threshold = item?.threshold || '100';
        item.priority = `${item?.priority || ''}`;
        item.contactEmail = `${item?.contactEmail || ''}`;
        item.studyStaffEmails = `${item?.studyStaffEmails || ''}`;
        item.UpdatedBy = userName;
      });

      // Save sites in batches
      let saveSitesOperation = true;
      let saveCount = 0;
      const batchSize = SmaBatchSize;
      for (let i = 0; i < sitesToSave.length; i += batchSize) {
        const batch = sitesToSave.slice(i, i + batchSize);
        const result = await saveSites(batch);
        if (!result) {
          saveSitesOperation = false;
          break;
        } else {
          saveCount += batch.length;
        }
      }

      if (!saveSitesOperation) {
        props.setIsLoading(false);
        setAlertModal({
          open: true,
          message:
            'Save and Publish failed: There was an issue while attempting to save the site(s) to staging. The site(s) could not be saved at this time. Please review the site data and try again.',
          title: 'Save Failed',
        });
        return;
      }

      // Store current unsaved edited rows before refetching (reused after publish)
      const currentUnsavedEditedRows = props.data.filter(
        (item) => editedSet.has(item.uid) && !uidsToSaveSet.has(item.uid)
      );

      // Remove saved UIDs from editedUids
      props.setEditedUids((prev) => prev.filter((uid) => !uidsToSaveSet.has(uid)));
      let finalData: ISiteMngData[] = [];
      // Refetch Data after save
      if (selectedProtocol && selectedFilters.language) {
        const refetchedData = await getSites(
          selectedProtocol,
          selectedFilters.language,
          selectedFilters.EPR
        );

        finalData = mergeRefetchedWithUnsaved(refetchedData, currentUnsavedEditedRows);
        props.setData(finalData);
        props.setCloneData(finalData);
        props.setRowSelection({});

        if (props.onDataRefetched) {
          props.onDataRefetched(uidsToSave);
        }
      }

      // Step 2: Wait 2 seconds
      //await sleep(5000);

      // Step 3: Publish the saved sites
      const savedSmaRowIdsSet = new Set(sitesToSave.map((item) => item.smaRowId));
      const sitesToPublish = finalData.filter((item) => {
        // @ts-ignore
        return item.source === 'staging' && savedSmaRowIdsSet.has(item.smaRowId);
      });

      if (sitesToPublish.length === 0) {
        props.setIsLoading(false);
        setAlertModal({
          open: true,
          message:
            'Publish failed: The saved sites could not be found for publishing. Please try saving and publishing separately.',
          title: 'Publish Failed',
        });
        return;
      }

      // Add Priority validation
      const rowsWithPriorityZero = sitesToPublish?.filter((item) => item?.priority === 0);
      if (rowsWithPriorityZero?.length > 0) {
        const siteNames = rowsWithPriorityZero.map((item) => item?.siteName).join(', ');
        props.setIsLoading(false);
        setAlertModal({
          open: true,
          message: `Priority validation failed: The following site(s) have a Priority value of 0, which is not allowed:\n${siteNames}\n\nPlease update the Priority values for these sites to a valid number (1-9) before proceeding with the publication.`,
          title: 'Priority Validation Failed',
        });
        return;
      }

      // Publish sites in batches
      sitesToPublish.forEach((item: any) => {
        item.isPublished = true;
        item.siteDetailTarget = selectedEpr;
        item.EprId = selectedFilters.EPR === 'global' ? null : selectedFilters.EPR;
        if (selectedFilters.EPR === 'global') {
          item.ProtocolItemId = selectedProtocolItemId;
        }
        item.radiusInMiles = item?.radiusInMiles || '100';
        item.threshold = item?.threshold || '100';
        item.priority = `${item?.priority || ''}`;
        item.contactEmail = item?.contactEmail || '';
        item.studyStaffEmails = item?.studyStaffEmails || '';
        item.UpdatedBy = userName;
      });

      let publishSitesOperation = true;
      let publishCount = 0;
      for (let i = 0; i < sitesToPublish.length; i += batchSize) {
        const batch = sitesToPublish.slice(i, i + batchSize);
        const result = await saveSites(batch);
        if (!result) {
          publishSitesOperation = false;
          break;
        } else {
          publishCount += batch.length;
        }
      }

      if (publishSitesOperation) {
        // Remove published UIDs from editedUids (currentUnsavedEditedRows already computed above)
        props.setEditedUids((prev) => prev.filter((uid) => !uidsToSaveSet.has(uid)));

        // Refetch Data after publish
        if (selectedProtocol && selectedFilters.language) {
          const refetchedDataAfterPublish = await getSites(
            selectedProtocol,
            selectedFilters.language,
            selectedFilters.EPR
          );
          await sleep(500);

          const finalDataAfterPublish = mergeRefetchedWithUnsaved(
            refetchedDataAfterPublish,
            currentUnsavedEditedRows
          );
          props.setData(finalDataAfterPublish);

          if (props.onDataRefetched) {
            props.onDataRefetched(uidsToSave);
          }
        }

        props.setRowSelection({});
        props.setIsLoading(false);

        // Show success alert
        setAlertModal({
          open: true,
          message: `Success! ${saveCount} Site(s) have been successfully saved to staging and ${publishCount} Site(s) have been published to Sitecore. The changes are now live on the website and visible to all users.${
            nonEditedSelectedRows.length > 0
              ? ` Note: ${nonEditedSelectedRows.length} selected site(s) were not saved or published as they had no edits.`
              : ''
          }`,
          title: 'Save and Publish Successful',
        });
      } else {
        props.setIsLoading(false);
        setAlertModal({
          open: true,
          message:
            'Save and Publish partially completed: The sites were saved successfully, but there was an issue while attempting to publish them to Sitecore. Please try publishing the sites separately.',
          title: 'Publish Failed',
        });
      }
    } catch (error) {
      props.setIsLoading(false);
      setAlertModal({
        open: true,
        message:
          'An unexpected error occurred while attempting to save and publish the sites. Please try again, or contact the Support team if the issue continues.',
        title: 'Error',
      });
    }
  };

  const onPublish = async () => {
    try {
      const selectedRowIds = Object.keys(rowSelection);
      if (selectedRowIds.length < 1) {
        alert('No sites are selected. Please select the Sites to be Published');
        return;
      }
      if (Array.isArray(props.data)) {
        const approvedRows = props.data.filter((item) => {
          // @ts-ignore
          return item.source === 'staging' && selectedRowIds.includes(item.uid);
        });
        //Add Priority validation
        const rowsWithPriorityZero = approvedRows?.filter((item) => item?.priority === 0);
        if (rowsWithPriorityZero?.length > 0) {
          const siteNames = rowsWithPriorityZero.map((item) => item?.siteName).join(', ');
          alert(
            `The following sites have a Priority value of 0:\n${siteNames}\n\nPlease update their Priority values to proceed.`
          );
          return;
        }
        //Add no of sites selected
        const confirmPopup = confirm(
          `Publishing ${approvedRows.length} Sites to Sitecore. Changes will be visible on the Website. Proceed?`
        );
        if (confirmPopup && approvedRows.length > 0) {
          props.setIsLoading(true);
          const EprId = selectedFilters.EPR === 'global' ? null : selectedFilters.EPR;
          let protocolItemId: any = null;
          if (selectedFilters.EPR === 'global') {
            protocolItemId = selectedProtocolItemId;
          }
          approvedRows.forEach((item: any) => {
            item.isPublished = true;
            item.siteDetailTarget = selectedEpr;
            item.EprId = EprId;
            item.radiusInMiles = item?.radiusInMiles || '100';
            item.threshold = item?.threshold || '100';
            item.priority = `${item?.priority || ''}`;
            item.contactEmail = item?.contactEmail || '';
            item.studyStaffEmails = item?.studyStaffEmails || '';
            item.ProtocolItemId = protocolItemId;
            item.UpdatedBy = userName;
          });
          // Save approvedRows in batches of 5
          let publishSitesOperation = true;
          let count = 0;
          const batchSize = SmaBatchSize;
          for (let i = 0; i < approvedRows.length; i += batchSize) {
            const batch = approvedRows.slice(i, i + batchSize);
            const result = await saveSites(batch);
            if (!result) {
              publishSitesOperation = false;
              break;
            } else {
              count += batch.length;
            }
          }
          if (publishSitesOperation) {
            // Refetch Data
            if (selectedProtocol && selectedFilters.language) {
              const refetchedData = await getSites(
                selectedProtocol,
                selectedFilters.language,
                selectedFilters.EPR
              );
              await sleep(500);
              props.setCloneData(refetchedData);
              props.setData(refetchedData);
              props.setRowSelection({});
            }
            props.setIsLoading(false);
            alert(`${count} Sites Published`);
          } else {
            props.setIsLoading(false);
            logger.error('Unable to process all sites to publishing.', {
              respose: `Only ${approvedRows.length - count} sites has been published`,
              payload: {},
            });
            alert(
              'There was an issue with publishing. The site(s) could not be published. Please contact the Support team for further assistance.'
            );
          }
          props.setIsLoading(false);
        } else {
          props.setIsLoading(false);
          alert('Only the sites under Staging can be published');
        }
      } else {
        props.setIsLoading(false);
        alert('Please contact the Support team for further assistance.');
        return;
      }
    } catch (error) {
      props.setIsLoading(false);
      alert('Error Publishing Sites');
    }
  };

  /**
   * Translate selected rows.
   */
  const onTranslateClick = async () => {
    const lang = selectedFilters.language;
    try {
      let uidsToFilter: string[] = [];
      const isRowsSelected = Object.keys(rowSelection).length > 0;
      if (isRowsSelected) {
        uidsToFilter = Object.keys(rowSelection);
      }
      if (uidsToFilter.length > 0) {
        const sitesToTranslate = props.data.filter((item) => uidsToFilter.includes(item.uid));
        const fieldOptionsToCheck =
          selectedFilters.EPR === 'global' ? fieldOptions : eprFieldOptions;
        const formatedSites = formatSitesForTranslation(
          sitesToTranslate,
          lang,
          fieldOptionsToCheck
        );
        props.setIsLoading(true);
        const translateSitesOperation = await translateSites(formatedSites);
        if (translateSitesOperation.sitesDataRequest) {
          props.setRowSelection({});
          const clonedTranslatedRows: ISiteMngData[] = [];
          props.data.forEach((item) => {
            translateSitesOperation.sitesDataRequest.forEach((resObj: any) => {
              if (item.uid === resObj.id) {
                const newSmaRowId = uuidv4();
                const newUid = `${newSmaRowId}_${SOURCE_SMA}`;
                const clonedItem = cloneDeep(item);
                const newItem = { ...clonedItem, ...resObj };
                delete newItem.id;
                newItem.subRows = [];
                newItem.smaRowId = newSmaRowId;
                newItem.source = SOURCE_SMA;
                newItem.uid = newUid;
                newItem.language = selectedFilters.language;
                clonedTranslatedRows.push(newItem);
                props.setEditedUids((arr) => [...arr, newUid]);
              }
            });
          });
          props.setData([...clonedTranslatedRows, ...props.data]);
          props.setIsLoading(false);
        }
        props.setIsLoading(false);
      } else {
        props.setIsLoading(false);
        alert('No Sites selected to Translate');
      }
    } catch (_error) {
      props.setIsLoading(false);
    }
  };
  const isBtnDisabled = Array.isArray(props.data) && props.data.length < 1;

  return (
    <>
      <TranslateModal
        isOpen={isTranslateOpen}
        setOpen={setIsTranslateOpen}
        handleTranslateClick={onTranslateClick}
      />
      <AlertModal
        open={alertModal.open}
        message={alertModal.message}
        title={alertModal.title}
        onClose={() => setAlertModal({ open: false, message: '' })}
      />
      <Box width="50%">
        <Stack spacing={3} direction="row">
          <Button
            variant="contained"
            disabled={isBtnDisabled}
            fullWidth
            onClick={() => onSaveClick(false)}
          >
            Save
          </Button>
          {props.role &&
            (props.role.includes(Roles.ADMIN) || props.role.includes(Roles.APPROVER)) && (
              <Button
                variant="contained"
                disabled={isBtnDisabled}
                fullWidth
                onClick={() => onPublish()}
              >
                Publish
              </Button>
            )}
          {props.role &&
            (props.role.includes(Roles.ADMIN) || props.role.includes(Roles.APPROVER)) && (
              <Button
                variant="contained"
                disabled={isBtnDisabled}
                fullWidth
                onClick={() => onSaveAndPublishClick()}
              >
                Save and Publish
              </Button>
            )}
          {selectedFilters.language !== 'en' && selectedFilters.EPR === EPR_TYPE.Global && (
            <Button
              variant="contained"
              disabled={isBtnDisabled}
              fullWidth
              onClick={() => setIsTranslateOpen(true)}
            >
              Translate
            </Button>
          )}
        </Stack>
      </Box>
    </>
  );
};

export default FooterActions;
